import React, { useState, useEffect } from 'react';
import './VisaPage.css';
import { VISA_DATA } from '../data/VisaData';
import { COMPANY } from '../data/ItineraryData';
import { supabase } from '../lib/supabase';


/* ── Type badge meta ─── */
const TYPE_META = {
    'Free on Arrival': { bg: '#d1fae5', color: '#065f46', icon: 'fa-solid fa-gift' },
    'Visa on Arrival': { bg: '#dbeafe', color: '#1e40af', icon: 'fa-solid fa-plane-arrival' },
    'e-Visa': { bg: '#ede9fe', color: '#5b21b6', icon: 'fa-solid fa-laptop' },
    'eNTRI / eVisa': { bg: '#ede9fe', color: '#5b21b6', icon: 'fa-solid fa-laptop' },
    'Schengen Visa': { bg: '#fef3c7', color: '#92400e', icon: 'fa-solid fa-star' },
    'Sticker Visa': { bg: '#fee2e2', color: '#991b1b', icon: 'fa-solid fa-stamp' },
    'B1/B2 Visa': { bg: '#fce7f3', color: '#9d174d', icon: 'fa-solid fa-id-card' },
    'TRV (Sticker Visa)': { bg: '#fee2e2', color: '#991b1b', icon: 'fa-solid fa-stamp' },
    'Standard Visitor Visa': { bg: '#f0fdf4', color: '#14532d', icon: 'fa-solid fa-passport' },
};
const typeMeta = (t) => TYPE_META[t] || { bg: '#f3f4f6', color: '#374151', icon: 'fa-solid fa-passport' };

const Flag = ({ code }) => (
    <img
        src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
        alt={code}
        className="vp-flag"
        onError={e => { e.target.style.display = 'none'; }}
    />
);

/* ════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════ */
const VisaPage = () => {
    const [selected, setSelected] = useState(null);
    const [showInquiry, setShowInquiry] = useState(false);
    const [inquiryVisa, setInquiryVisa] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({ name: '', phone: '', email: '', country: '', travelDate: '', notes: '' });
    const [visaList, setVisaList] = useState(VISA_DATA); // start with static, replace with live
    const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // Fetch live data from Supabase; fall back to static if empty
    useEffect(() => {
        supabase.from('visa_cards').select('*').order('display_order').then(({ data }) => {
            if (data && data.length > 0) {
                // Normalise: Supabase uses fee_inr, static data uses feeINR
                const normalised = data.map(v => ({ ...v, feeINR: v.fee_inr ?? v.feeINR ?? 0 }));
                setVisaList(normalised);
            }
        });
    }, []);

    const popular = visaList.filter(v => v.popular);


    const openInquiry = (visa = null) => {
        setInquiryVisa(visa);
        setForm(f => ({ ...f, country: visa ? visa.country : '' }));
        setSubmitted(false);
        setSelected(null);
        setShowInquiry(true);
    };

    const handleInquiry = async (e) => {
        e.preventDefault();
        await supabase.from('inquiries').insert([{
            type: 'visa',
            name: form.name,
            phone: form.phone,
            email: form.email,
            country: inquiryVisa ? inquiryVisa.country : form.country,
            travel_date: form.travelDate || null,
            notes: form.notes,
            status: 'new',
        }]);
        setSubmitted(true);
        setTimeout(() => {
            setSubmitted(false);
            setShowInquiry(false);
            setForm({ name: '', phone: '', email: '', country: '', travelDate: '', notes: '' });
        }, 3000);
    };

    return (
        <div className="vp-page">

            {/* ══ HERO ═══════════════════════════════════════ */}
            <div className="vp-hero">
                <div className="vp-hero-bg" />
                <div className="vp-hero-content">
                    <div className="vp-hero-badge"><i className="fa-solid fa-passport"></i> Visa Services</div>
                    <h1 className="vp-hero-title">Travel Visa Made Simple</h1>
                    <p className="vp-hero-sub">Tourist visas for 25+ countries — fast processing, expert guidance, zero hassle</p>

                    <div className="vp-stats">
                        {[
                            { n: '25+', l: 'Countries' },
                            { n: '98%', l: 'Success Rate' },
                            { n: '3–5', l: 'Days Avg Processing' },
                            { n: '2000+', l: 'Visas Processed' },
                        ].map((s, i) => (
                            <div key={i} className="vp-stat">
                                <span className="vp-stat-n">{s.n}</span>
                                <span className="vp-stat-l">{s.l}</span>
                            </div>
                        ))}
                    </div>

                    <button className="vp-hero-inquiry-btn" onClick={() => openInquiry()}>
                        <i className="fa-solid fa-paper-plane"></i> Get Visa Assistance
                    </button>
                </div>
            </div>

            {/* ══ POPULAR PICKS ══════════════════════════════ */}
            <div className="vp-section vp-popular-section">
                <div className="vp-section-inner">
                    <h2 className="vp-section-title"><i className="fa-solid fa-fire"></i> Popular Destinations</h2>
                    <div className="vp-popular-grid">
                        {popular.map((v, i) => (
                            <div
                                key={i}
                                className="vp-popular-card"
                                style={{ '--accent': v.color }}
                                onClick={() => setSelected(v)}
                            >
                                <div className="vp-pc-top">
                                    <Flag code={v.flag} />
                                    <div className="vp-pc-type" style={{ background: typeMeta(v.type).bg, color: typeMeta(v.type).color }}>
                                        <i className={typeMeta(v.type).icon}></i> {v.type}
                                    </div>
                                </div>
                                <div className="vp-pc-country">{v.country}</div>
                                <div className="vp-pc-meta">
                                    <span><i className="fa-solid fa-clock"></i> {v.processing}</span>
                                    <span><i className="fa-solid fa-calendar"></i> {v.validity}</span>
                                    <span><i className="fa-solid fa-layer-group"></i> {v.entries}</span>
                                </div>
                                <button className="vp-pc-btn" onClick={e => { e.stopPropagation(); openInquiry(v); }}>
                                    <i className="fa-solid fa-paper-plane"></i> Inquire
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ══ PROCESS STEPS ══════════════════════════════ */}
            <div className="vp-process-section">
                <div className="vp-section-inner">
                    <h2 className="vp-section-title" style={{ color: '#fff' }}><i className="fa-solid fa-list-check"></i> How It Works</h2>
                    <div className="vp-steps">
                        {[
                            { step: 1, icon: 'fa-solid fa-search', title: 'Choose Country', desc: 'Select your destination from our list & tell us your travel dates' },
                            { step: 2, icon: 'fa-solid fa-file-lines', title: 'Submit Documents', desc: 'Share required documents — we review & verify everything for you' },
                            { step: 3, icon: 'fa-solid fa-paper-plane', title: 'Application Filed', desc: 'We file & track your application with the embassy on your behalf' },
                            { step: 4, icon: 'fa-solid fa-passport', title: 'Visa Received', desc: 'Visa delivered digitally or by courier directly to your doorstep' },
                        ].map(s => (
                            <div key={s.step} className="vp-step">
                                <div className="vp-step-num">{s.step}</div>
                                <div className="vp-step-icon"><i className={s.icon}></i></div>
                                <div className="vp-step-title">{s.title}</div>
                                <div className="vp-step-desc">{s.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ══ CTA SECTION ════════════════════════════════ */}
            <div className="vp-cta-section">
                <div className="vp-section-inner vp-cta-inner">
                    <div>
                        <h2 className="vp-cta-title">Ready to Apply for Your Visa?</h2>
                        <p className="vp-cta-sub">Our visa experts are available Mon–Sat, 9 AM to 8 PM to guide you through every step</p>
                    </div>
                    <div className="vp-cta-contacts">
                        <button className="vp-cta-btn vp-cta-inquiry" onClick={() => openInquiry()}>
                            <i className="fa-solid fa-paper-plane"></i> Submit Visa Inquiry
                        </button>
                        <a href={`tel:${COMPANY.phone}`} className="vp-cta-btn vp-cta-call">
                            <i className="fa-solid fa-phone"></i> {COMPANY.phone}
                        </a>
                        <a href={`mailto:${COMPANY.email}`} className="vp-cta-btn vp-cta-email">
                            <i className="fa-solid fa-envelope"></i> Email Us
                        </a>
                    </div>
                </div>
            </div>

            {/* ══ DETAIL MODAL ═══════════════════════════════ */}
            {selected && (
                <div className="vp-modal-overlay" onClick={() => setSelected(null)}>
                    <div className="vp-modal" onClick={e => e.stopPropagation()}>
                        <button className="vp-modal-close" onClick={() => setSelected(null)}>
                            <i className="fa-solid fa-xmark"></i>
                        </button>

                        <div className="vp-modal-header" style={{ '--accent': selected.color }}>
                            <Flag code={selected.flag} />
                            <div>
                                <div className="vp-modal-country">{selected.country}</div>
                                <div className="vp-modal-region">{selected.region}</div>
                                <span className="vp-type-badge" style={{ background: typeMeta(selected.type).bg, color: typeMeta(selected.type).color }}>
                                    <i className={typeMeta(selected.type).icon}></i> {selected.type}
                                </span>
                            </div>
                        </div>

                        <div className="vp-modal-body">
                            <div className="vp-modal-info-grid">
                                {[
                                    { l: 'Processing', v: selected.processing },
                                    { l: 'Validity', v: selected.validity },
                                    { l: 'Entries', v: selected.entries },
                                ].map((r, i) => (
                                    <div key={i} className="vp-info-cell">
                                        <span className="vp-info-label">{r.l}</span>
                                        <span className="vp-info-val">{r.v}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="vp-modal-docs">
                                <h4><i className="fa-solid fa-folder-open"></i> Required Documents</h4>
                                <ul>
                                    {selected.docs.map((d, i) => (
                                        <li key={i}><i className="fa-solid fa-check"></i> {d}</li>
                                    ))}
                                </ul>
                            </div>

                            <button className="vp-modal-apply-btn" onClick={() => openInquiry(selected)}>
                                <i className="fa-solid fa-paper-plane"></i> Inquire for {selected.country} Visa
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══ INQUIRY FORM MODAL ═════════════════════════ */}
            {showInquiry && (
                <div className="vp-modal-overlay" onClick={() => { setShowInquiry(false); setSubmitted(false); }}>
                    <div className="vp-modal vp-inq-modal" onClick={e => e.stopPropagation()}>
                        <button className="vp-modal-close" onClick={() => { setShowInquiry(false); setSubmitted(false); }}>
                            <i className="fa-solid fa-xmark"></i>
                        </button>

                        {submitted ? (
                            <div className="vp-inq-success">
                                <div className="vp-inq-ok-icon"><i className="fa-solid fa-circle-check"></i></div>
                                <h3>Inquiry Submitted!</h3>
                                <p>Our visa team will contact you within <strong>2 hours</strong>.</p>
                            </div>
                        ) : (
                            <>
                                <div className="vp-inq-header">
                                    {inquiryVisa && <Flag code={inquiryVisa.flag} />}
                                    <div>
                                        <h3>{inquiryVisa ? `${inquiryVisa.country} Visa Inquiry` : 'Visa Inquiry'}</h3>
                                        <p>{inquiryVisa
                                            ? `${inquiryVisa.type} · ${inquiryVisa.processing} · ${inquiryVisa.validity}`
                                            : 'Tell us which country you need a visa for'}
                                        </p>
                                    </div>
                                </div>
                                <form className="vp-inq-form" onSubmit={handleInquiry}>
                                    <div className="vp-inq-grid">
                                        <div className="vp-inq-field">
                                            <label>Full Name *</label>
                                            <input required type="text" placeholder="Your name"
                                                value={form.name} onChange={e => setF('name', e.target.value)} />
                                        </div>
                                        <div className="vp-inq-field">
                                            <label>Phone *</label>
                                            <input required type="tel" placeholder="+91 XXXXX XXXXX"
                                                value={form.phone} onChange={e => setF('phone', e.target.value)} />
                                        </div>
                                        <div className="vp-inq-field vp-inq-full">
                                            <label>Email *</label>
                                            <input required type="email" placeholder="you@email.com"
                                                value={form.email} onChange={e => setF('email', e.target.value)} />
                                        </div>
                                        {!inquiryVisa && (
                                            <div className="vp-inq-field vp-inq-full">
                                                <label>Country / Destination</label>
                                                <input type="text" placeholder="e.g. Dubai, Thailand, UK..."
                                                    value={form.country} onChange={e => setF('country', e.target.value)} />
                                            </div>
                                        )}
                                        <div className="vp-inq-field">
                                            <label>Expected Travel Date</label>
                                            <input type="date" value={form.travelDate}
                                                onChange={e => setF('travelDate', e.target.value)}
                                                min={new Date().toISOString().split('T')[0]} />
                                        </div>
                                        <div className="vp-inq-field vp-inq-full">
                                            <label>Notes / Questions</label>
                                            <textarea rows={3} placeholder="Number of travellers, special queries..."
                                                value={form.notes} onChange={e => setF('notes', e.target.value)} />
                                        </div>
                                    </div>
                                    <button type="submit" className="vp-inq-submit">
                                        <i className="fa-solid fa-paper-plane"></i> Submit Inquiry
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
};

export default VisaPage;
